import type { UpdateSparkActivityPayload } from '../../types/spark-activity';
import { useSparkPlays } from '../../hooks/useSparkPlays';
import { useChildPreferences } from '../../hooks/useChildPreferences';
import { usePackNames } from '../../hooks/usePackNames';
import ToggleChipGroup from './ToggleChipGroup';
import ActivityImageUpload from './ActivityImageUpload';

interface ActivityImageValue {
  documentId: number;
  viewUrl: string;
}

interface ClassificationPanelProps {
  formData: UpdateSparkActivityPayload;
  onChange: (field: keyof UpdateSparkActivityPayload, value: unknown) => void;
  imageValue: ActivityImageValue | null;
  onImageChange: (image: ActivityImageValue | null) => void;
}

export default function ClassificationPanel({
  formData,
  onChange,
  imageValue,
  onImageChange,
}: ClassificationPanelProps) {
  const { data: plays = [] } = useSparkPlays();
  const { data: preferences = [] } = useChildPreferences();
  const { data: packs = [] } = usePackNames();

  const packItems = packs.map((p) => ({ id: p.id, title: p.name }));

  return (
    <div className="bg-[#F4F9F6] border-[1.5px] border-pine-line rounded-[12px] p-[8px_20px_20px] mt-[26px]">
      {/* <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-pine-pine mb-[14px]">
        Classification
      </p> */}

      <div className="flex flex-col gap-[26px] lg:flex-row">
        {/* Classification chips */}
        <div className="flex-1 min-w-0">
          {/* Play Style */}
          <div data-testid="play-style-section">
            <ToggleChipGroup
              label="Play Style"
              items={plays}
              selectedIds={formData.playIds}
              onChange={(ids) => onChange('playIds', ids)}
            />
          </div>

          {/* Child Preferences */}
          <div data-testid="preferences-section">
            <ToggleChipGroup
              label="Child Preferences"
              items={preferences}
              selectedIds={formData.preferenceIds}
              onChange={(ids) => onChange('preferenceIds', ids)}
            />
          </div>

          {/* Physical Deck / Packs */}
          <div data-testid="packs-section">
            <ToggleChipGroup
              label="Physical Deck"
              secondaryLabel="(Packs)"
              items={packItems}
              selectedIds={formData.packIds}
              onChange={(ids) => onChange('packIds', ids)}
            />
          </div>
        </div>

        {/* Activity Image */}
        <div
          data-testid="activity-image-section"
          className="lg:w-[180px] lg:flex-shrink-0 lg:border-l lg:border-pine-line lg:pl-[26px]"
        >
          <p className="mt-[14px] text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mb-2">
            Activity Image
          </p>
          <ActivityImageUpload value={imageValue} onChange={onImageChange} />
        </div>
      </div>
    </div>
  );
}
